import { useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  LayoutGrid,
  ClipboardList,
  ImageIcon,
  PlayCircle,
  ShieldCheck,
  LifeBuoy,
  Bug,
  UserCog,
  Lightbulb,
  ArrowUpRight,
  type LucideIcon,
} from 'lucide-react';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { PageContainer, PageHeader } from '@/components/layout/PageContainer';
import { OverviewTab } from './tabs/OverviewTab';
import { PlannerTab } from './tabs/PlannerTab';
import { CreativeAgencyTab } from './tabs/CreativeAgencyTab';
import { ActivationTab } from './tabs/ActivationTab';
import { ApproverTab } from './tabs/ApproverTab';

// External Jira forms (Content Tracker / Asset QC) for centralised support requests.
const BUG_FEEDBACK_FORM_URL =
  'https://wundertracker.atlassian.net/jira/software/c/form/923bfa25-ee1b-4869-886c-e2ad910a33b2';
const USER_ACCESS_FORM_URL =
  'https://wundertracker.atlassian.net/jira/software/c/form/c4c9fe71-8a52-4272-8661-7d242c13ebff';
const IDEA_FORM_URL =
  'https://forms.office.com/Pages/ResponsePage.aspx?id=oV91K9Ej80iY_G_cHcSNaZ7XcrZDi5lOrSo7q3RTX2VUQ1RNQTQ4WVlCSFVYQ01JVEZLVTQ5NDFPOC4u';

type TabKey = 'overview' | 'planner' | 'creative' | 'activation' | 'approver';

interface Tab {
  key: TabKey;
  label: string;
}

const ALL_TABS: Tab[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'planner', label: 'Planner' },
  { key: 'creative', label: 'Creative Agency' },
  { key: 'activation', label: 'Activation' },
  { key: 'approver', label: 'Approver' },
];

const TAB_CONFIG: Record<TabKey, { icon: LucideIcon; activeColor: string; activeBorder: string }> =
  {
    overview: { icon: LayoutGrid, activeColor: 'text-[#0014cc]', activeBorder: 'border-[#0014cc]' },
    planner: { icon: ClipboardList, activeColor: 'text-blue-800', activeBorder: 'border-blue-800' },
    creative: {
      icon: ImageIcon,
      activeColor: 'text-purple-600',
      activeBorder: 'border-purple-600',
    },
    activation: {
      icon: PlayCircle,
      activeColor: 'text-green-700',
      activeBorder: 'border-green-700',
    },
    approver: {
      icon: ShieldCheck,
      activeColor: 'text-orange-700',
      activeBorder: 'border-orange-700',
    },
  };

export const AboutPage = () => {
  const [activeTab, setActiveTab] = useState<TabKey>(() => {
    const stored = localStorage.getItem('about-active-tab') as TabKey | null;
    if (stored && ALL_TABS.some((t) => t.key === stored)) return stored;
    return 'overview';
  });

  const handleTabChange = useCallback((key: TabKey) => {
    setActiveTab(key);
    localStorage.setItem('about-active-tab', key);
    window.scrollTo(0, 0);
  }, []);

  const supportBanner = (
    <div className="px-12 mt-1">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <LifeBuoy className="w-4 h-4 text-[#0014cc] shrink-0" strokeWidth={2} />
          <span className="text-sm font-medium text-slate-700">Need help?</span>
          <span className="hidden text-xs text-slate-400 sm:inline">
            Report a bug, share feedback, or add, remove, or change access for a user.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={BUG_FEEDBACK_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0014cc] px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-[#0014cc]/90"
          >
            <Bug className="w-3.5 h-3.5" strokeWidth={2} />
            Report Bug
            <ArrowUpRight className="w-3.5 h-3.5 opacity-80" strokeWidth={2} />
          </a>
          <a
            href={USER_ACCESS_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-all hover:border-[#0014cc] hover:text-[#0014cc] hover:shadow-sm"
          >
            <UserCog className="w-3.5 h-3.5" strokeWidth={2} />
            Request User Access
            <ArrowUpRight className="w-3.5 h-3.5 opacity-60" strokeWidth={2} />
          </a>
          {IDEA_FORM_URL ? (
            <a
              href={IDEA_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-all hover:border-[#0014cc] hover:text-[#0014cc] hover:shadow-sm"
            >
              <Lightbulb className="w-3.5 h-3.5" strokeWidth={2} />
              Submit an Idea
              <ArrowUpRight className="w-3.5 h-3.5 opacity-60" strokeWidth={2} />
            </a>
          ) : (
            <button
              type="button"
              disabled
              className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-400"
            >
              <Lightbulb className="w-3.5 h-3.5" strokeWidth={2} />
              Submit an Idea
              <span className="sr-only"> (Coming soon)</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const tabStrip = (
    <div className="mt-3 flex justify-center">
      <div className="flex items-end gap-0 border-b border-slate-200" role="tablist">
        {ALL_TABS.map(({ key, label }) => {
          const { icon: Icon, activeColor, activeBorder } = TAB_CONFIG[key];
          const isActive = activeTab === key;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${key}`}
              onClick={() => handleTabChange(key)}
              className={`flex items-center gap-1.5 px-4 pb-2.5 pt-1 text-sm font-medium border-b-2 -mb-px transition-all whitespace-nowrap ${
                isActive
                  ? `${activeBorder} ${activeColor}`
                  : 'border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300'
              }`}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={2} />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <PageHeader>
        <Breadcrumb
          actions={<img src="/vml-logo-dark.svg" alt="VML MAP" className="h-6 ml-auto" />}
        />
        {supportBanner}
        {tabStrip}
      </PageHeader>

      <PageContainer align>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            role="tabpanel"
            id={`tabpanel-${activeTab}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
          >
            {activeTab === 'overview' && <OverviewTab />}
            {activeTab === 'planner' && <PlannerTab />}
            {activeTab === 'creative' && <CreativeAgencyTab />}
            {activeTab === 'activation' && <ActivationTab />}
            {activeTab === 'approver' && <ApproverTab />}
          </motion.div>
        </AnimatePresence>
      </PageContainer>
    </>
  );
};
